import React, { useEffect, useRef } from 'react';
import { LinkIcon, MessageCircleIcon, XIcon } from 'lucide-react';

interface CampaignShareMenuProps {
  isDesktopOpen: boolean;
  isMobileOpen: boolean;
  copied: boolean;
  onClose: () => void;
  onShareOption: (method: 'x' | 'facebook' | 'text' | 'copy_link') => void;
}

const CampaignShareMenu: React.FC<CampaignShareMenuProps> = ({
  isDesktopOpen,
  isMobileOpen,
  copied,
  onClose,
  onShareOption,
}) => {
  const shareMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isDesktopOpen && !isMobileOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!shareMenuRef.current) return;
      if (shareMenuRef.current.contains(event.target as Node)) return;
      onClose();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDesktopOpen, isMobileOpen, onClose]);

  if (!isDesktopOpen && !isMobileOpen) {
    return null;
  }

  return (
    <div ref={shareMenuRef}>
      {isDesktopOpen && (
        <div
          className="absolute left-0 top-full z-30 mt-2 hidden min-w-[220px] rounded-xl border border-gray-200 bg-white p-2 shadow-lg md:block"
          role="menu"
          aria-label="Share options"
        >
          <button
            type="button"
            onClick={() => onShareOption('x')}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            role="menuitem"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-800">
              X
            </span>
            <span>Share on X</span>
          </button>

          <button
            type="button"
            onClick={() => onShareOption('facebook')}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            role="menuitem"
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-800">
              f
            </span>
            <span>Share on Facebook</span>
          </button>

          <button
            type="button"
            onClick={() => onShareOption('text')}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            role="menuitem"
          >
            <MessageCircleIcon className="h-4 w-4 text-gray-600" />
            <span>Text message</span>
          </button>

          <button
            type="button"
            onClick={() => onShareOption('copy_link')}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-gray-700 transition hover:bg-gray-50"
            role="menuitem"
          >
            <LinkIcon className="h-4 w-4 text-gray-600" />
            <span>{copied ? 'Copied!' : 'Copy link'}</span>
          </button>
        </div>
      )}

      {isMobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden">
          <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-textPrimary">Share this issue</h3>
                <p className="text-sm text-gray-500">Help more people discover this campaign.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 text-gray-500 transition hover:bg-gray-100"
                aria-label="Close share options"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => onShareOption('x')}
                className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-800">
                  X
                </span>
                Share on X
              </button>

              <button
                type="button"
                onClick={() => onShareOption('facebook')}
                className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-800">
                  f
                </span>
                Share on Facebook
              </button>

              <button
                type="button"
                onClick={() => onShareOption('text')}
                className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                <MessageCircleIcon className="h-5 w-5 text-gray-600" />
                Text message
              </button>

              <button
                type="button"
                onClick={() => onShareOption('copy_link')}
                className="flex w-full items-center gap-3 rounded-xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                <LinkIcon className="h-5 w-5 text-gray-600" />
                {copied ? 'Copied!' : 'Copy link'}
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="mt-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignShareMenu;